import { http } from '@/lib/http';
import type { DateOnly, DateRangeParams, Decimal, PagedResult, RowVersion, UtcDateTime } from './types';

/**
 * Commission module (M5) — mirrors apps/api/src/Crm.Api/Modules/Commission (CommissionEndpoints.cs + DTO records).
 * All decimals are strings (global DecimalStringConverter, including inside RuleJson). Every money/approval POST
 * the API marks `.RequireIdempotency()` takes an `idempotencyKey` generated once per user intent.
 */
const base = '/commissions';

/** Permission codes (CommissionModule.CommissionPerm). */
export const CP = {
  policyRead: 'commission.policy.read',
  policyManage: 'commission.policy.manage',
  accrualReadOwn: 'commission.accrual.read.own',
  accrualReadAll: 'commission.accrual.read.all',
  adjustmentRequest: 'commission.adjustment.request',
  scheduleManage: 'commission.schedule.manage',
  payoutManage: 'commission.payout.manage',
  payoutVerify: 'commission.payout.verify',
} as const;

/** Status-catalog domains. Adjustments reuse APPROVAL_INSTANCE codes (no own domain in doc 03). */
export const CD = {
  policy: 'COMMISSION_POLICY',
  accrual: 'COMMISSION_ACCRUAL',
  schedule: 'COMMISSION_PAYOUT_SCHEDULE',
  payout: 'COMMISSION_PAYOUT',
  adjustment: 'APPROVAL_INSTANCE',
} as const;

// ---------------------------------------------------------------- RuleJson (CommissionRules.cs)

export const FORMULA_TYPES = ['RATE_ON_COLLECTED', 'TIERED', 'FIXED_PER_ITEM', 'RATE_ON_REVENUE', 'MARGIN_BASED'] as const;
export type FormulaType = (typeof FORMULA_TYPES)[number];
/** Formulas whose inputs exist today (FormulaTypes.Computable). Others can be saved as draft but never published. */
export const COMPUTABLE_FORMULAS: readonly string[] = ['RATE_ON_COLLECTED', 'FIXED_PER_ITEM', 'TIERED'];
export const TAX_BASES = ['BEFORE_VAT', 'AFTER_VAT'] as const;
export const TRIGGERS = ['CONTRACT_APPROVED', 'PAYMENT_VERIFIED', 'MILESTONE_ACCEPTED', 'FULL_PAID'] as const;
export const CLAWBACK_MODES = ['REVERSE_ACCRUAL', 'CARRY_FORWARD_NEGATIVE'] as const;
export const TIER_BASES = ['CONTRACT', 'PERIOD'] as const;
export const SCHEDULE_TYPES = ['AFTER_RECEIPT', 'MONTHLY', 'MILESTONE'] as const;
export const PAYOUT_DAY_RULES = ['FIXED_DAY', 'BUSINESS_DAY'] as const;
export const HOLIDAY_ADJUSTMENTS = ['NEXT_BUSINESS_DAY', 'PREVIOUS_BUSINESS_DAY', 'NONE'] as const;
export const PAYEE_TYPES = ['SALE', 'TEAM', 'ALL'] as const;
export const ELIGIBILITY_CONDITIONS = ['NO_OPEN_DISPUTE', 'ACCEPTANCE_REQUIRED', 'CONTRACT_FULLY_PAID', 'ACCRUAL_APPROVED'] as const;
export const ADJUSTMENT_REASONS = ['CALCULATION_ERROR', 'MISSED_ACCRUAL', 'CLAWBACK', 'PAYOUT_REVERSAL', 'APPROVED_BONUS', 'OTHER'] as const;
/** Evidence accepted for a PAID verification (PayoutService.PayoutEvidenceTypes). */
export const PAYOUT_EVIDENCE_TYPES = ['BANK_STATEMENT', 'PAYMENT_PROOF'];

export interface PolicyScope {
  roleCodes?: string[] | null;
  saleUserIds?: number[] | null;
  teamIds?: number[] | null;
  serviceIds?: number[] | null;
  trainingCategoryCodes?: string[] | null;
  customerClassifications?: string[] | null;
  contractTypeCodes?: string[] | null;
}
export interface TierDef {
  from: Decimal;
  to?: Decimal | null;
  ratePercent: Decimal;
}
export interface PolicyFormula {
  type: string;
  ratePercent?: Decimal | null;
  fixedAmount?: Decimal | null;
  tiers?: TierDef[] | null;
  tierBasis?: string | null;
  minAmount?: Decimal | null;
  maxAmount?: Decimal | null;
}
export interface PolicyBase {
  taxBasis: string | null;
  excludeDiscounts?: boolean;
  excludeRefunds?: boolean;
  excludeFees?: boolean;
}
export interface PolicyTrigger {
  event: string;
}
export interface PolicyVesting {
  holdDays?: number;
  requireNoDispute?: boolean;
  requireAcceptance?: boolean;
}
export interface PolicyCadence {
  scheduleType?: string | null;
  cutoffDay?: number | null;
  payoutDay?: number | null;
  payoutMonthOffset?: number | null;
}
export interface PolicyThreshold {
  minPayout?: Decimal | null;
  capPerPeriod?: Decimal | null;
  capPerContract?: Decimal | null;
}
export interface CommissionRule {
  scope?: PolicyScope | null;
  formula: PolicyFormula;
  base: PolicyBase;
  trigger: PolicyTrigger;
  vesting?: PolicyVesting | null;
  cadence?: PolicyCadence | null;
  threshold?: PolicyThreshold | null;
  clawback?: { mode: string } | null;
  approval?: { accrualConfirmationRequired?: boolean; note?: string | null } | null;
}
export interface RuleError {
  field: string;
  message: string;
}

// ---------------------------------------------------------------- policies

export interface PolicyListItem {
  id: number;
  code: string;
  name: string;
  status: string;
  isDemo: boolean;
  currentVersionId: number | null;
  currentVersionNo: number | null;
  currentFormulaType: string | null;
  currentEffectiveFromUtc: UtcDateTime | null;
  openVersionId: number | null;
  openVersionStatus: string | null;
  version: RowVersion;
}
export interface PolicyVersionDto {
  id: number;
  policyId: number;
  versionNo: number;
  status: string;
  statusName: string;
  basisCode: string;
  formulaType: string;
  rule: CommissionRule;
  effectiveFromUtc: UtcDateTime;
  effectiveToUtc: UtcDateTime | null;
  effectiveEndUtc: UtcDateTime | null;
  timeZoneId: string;
  changeNote: string | null;
  copiedFromVersionId: number | null;
  approvalInstanceId: number | null;
  submittedBy: number | null;
  submittedAtUtc: UtcDateTime | null;
  publishedBy: number | null;
  publishedAtUtc: UtcDateTime | null;
  retiredAtUtc: UtcDateTime | null;
  retireReason: string | null;
  ruleHash: string | null;
  computable: boolean;
  publishBlockers: RuleError[];
  version: RowVersion;
}
export interface PolicyDetail {
  id: number;
  code: string;
  name: string;
  description: string | null;
  status: string;
  isDemo: boolean;
  currentVersionId: number | null;
  versions: PolicyVersionDto[];
  version: RowVersion;
}
export interface PolicyConflict {
  versionId: number;
  policyId: number;
  policyCode: string;
  versionNo: number;
  effectiveFromUtc: UtcDateTime;
  effectiveEndUtc: UtcDateTime | null;
}
export interface CreatePolicyRequest {
  code?: string | null;
  name: string;
  description?: string | null;
  rule: CommissionRule;
  effectiveFromUtc: UtcDateTime;
  effectiveToUtc?: UtcDateTime | null;
  timeZoneId?: string | null;
  changeNote?: string | null;
}
export interface UpdatePolicyVersionRequest extends Omit<CreatePolicyRequest, 'code'> {
  version: RowVersion;
}
export interface ApprovalInstanceRef {
  instanceId: number;
  status: string;
  policyVersionId: number;
}

// ---------------------------------------------------------------- accruals

export interface AccrualListItem {
  id: number;
  kind: 'ACCRUAL' | 'CLAWBACK' | string;
  saleUserId: number;
  saleName: string;
  contractId: number;
  receiptAllocationId: number | null;
  sourceType: string;
  sourceId: number;
  linkedAccrualId: number | null;
  policyVersionId: number;
  policyVersionNo: number;
  policyCode: string;
  formulaType: string;
  sourceAmount: Decimal;
  eligibleBase: Decimal;
  ratePercent: Decimal | null;
  amount: Decimal;
  currency: string;
  status: string;
  statusName: string;
  statusReason: string | null;
  occurredAtUtc: UtcDateTime;
  eligibleOn: DateOnly | null;
  formulaText: string;
  payoutId: number | null;
  payoutCode: string | null;
  payoutStatus: string | null;
  version: RowVersion;
}
export interface AccrualTraceStep {
  step: 'SOURCE' | 'POLICY' | 'CALCULATION' | 'CLAWBACK' | 'ADJUSTMENT' | 'PAYOUT' | 'VERIFICATION' | string;
  description: string;
  atUtc: UtcDateTime | null;
  actorUserId: number | null;
  reference: string | null;
}
export interface SourceEvent {
  id: number;
  outboxEventId: number;
  eventType: string;
  sourceType: string | null;
  sourceId: number | null;
  contractId: number | null;
  receiptId: number | null;
  saleUserId: number | null;
  amount: Decimal | null;
  currency: string | null;
  occurredAtUtc: UtcDateTime | null;
  processedAtUtc: UtcDateTime;
  outcome: string;
  detail: string | null;
}
/** Frozen InputsJson (AccrualEngine). Shape varies by source; every field is optional on the client. */
export interface AccrualInputs {
  source?: {
    type?: string;
    allocationId?: number;
    receiptId?: number;
    receivableId?: number;
    contractId?: number;
    customerId?: number;
    amount?: Decimal;
    currency?: string;
    verifiedAtUtc?: UtcDateTime;
    receivableFullyPaid?: boolean;
    outboxEventId?: number;
    id?: number;
  };
  policy?: { policyId?: number; code?: string; versionId?: number; versionNo?: number; ruleHash?: string; timeZoneId?: string };
  taxBasis?: string;
  contract?: { grossTotal?: Decimal; taxTotal?: Decimal; netTotal?: Decimal; currency?: string; contractTypeCode?: string } | null;
  formula?: PolicyFormula;
  prior?: { cumulativeBase?: Decimal; forContract?: Decimal; forPeriod?: Decimal };
  eligibleBase?: Decimal;
  computedAmount?: Decimal;
  cappedAmount?: Decimal;
  amount?: Decimal;
  effectiveRatePercent?: Decimal | null;
  tier?: string | null;
  formulaText?: string;
  notes?: string[];
  trigger?: { event?: string; satisfiedAtUtc?: UtcDateTime | null };
  vesting?: PolicyVesting;
  [key: string]: unknown;
}
export interface AccrualDetail {
  accrual: AccrualListItem;
  inputs: AccrualInputs;
  policyVersion: PolicyVersionDto | null;
  clawbacks: AccrualListItem[];
  adjustments: AdjustmentDto[];
  sourceEvent: SourceEvent | null;
  trace: AccrualTraceStep[];
}
/** dateField: occurredAt (default) | eligibleOn | createdAt (`fromUtc`/`toUtc` on occurredAt kept for back-compat). */
export interface AccrualFilters extends DateRangeParams {
  saleUserId?: number;
  status?: string;
  contractId?: number;
  policyVersionId?: number;
  kind?: string;
  fromUtc?: string;
  toUtc?: string;
  page?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------- adjustments

export interface AdjustmentDto {
  id: number;
  adjustmentCode: string;
  saleUserId: number;
  accrualId: number | null;
  payoutId: number | null;
  amount: Decimal;
  currency: string;
  reasonCode: string;
  reasonText: string;
  evidenceDocumentIds: number[];
  status: string;
  approvalInstanceId: number | null;
  createdBy: number | null;
  createdAtUtc: UtcDateTime;
  submittedBy: number | null;
  submittedAtUtc: UtcDateTime | null;
  decidedBy: number | null;
  decidedAtUtc: UtcDateTime | null;
  decisionReason: string | null;
  consumedByPayoutId: number | null;
  version: RowVersion;
  saleName?: string | null;
  createdByName?: string | null;
  submittedByName?: string | null;
  decidedByName?: string | null;
}
export interface CreateAdjustmentRequest {
  saleUserId: number;
  accrualId?: number | null;
  payoutId?: number | null;
  amount: Decimal;
  currency?: string | null;
  reasonCode: string;
  reasonText: string;
  evidenceDocumentIds?: number[] | null;
}

// ---------------------------------------------------------------- schedules

export interface ScheduleDto {
  id: number;
  scheduleCode: string;
  versionNo: number;
  name: string;
  payeeType: string;
  saleUserId: number | null;
  teamId: number | null;
  policyVersionId: number | null;
  scheduleType: string;
  calculationBasis: string;
  waitDays: number;
  cutoffDay: number;
  payoutDay: number;
  payoutMonthOffset: number;
  payoutDayRule: string;
  holidayAdjustment: string;
  timeZoneId: string;
  minPayoutAmount: Decimal | null;
  eligibilityConditions: string[];
  eligibilityConditionsVersion: number;
  effectiveFrom: DateOnly;
  effectiveTo: DateOnly | null;
  status: string;
  statusName: string;
  previousVersionId: number | null;
  changeReason: string | null;
  approvalInstanceId: number | null;
  approvalStatus: string | null;
  activatedAtUtc: UtcDateTime | null;
  statusReason: string | null;
  isDemo: boolean;
  createdAtUtc: UtcDateTime;
  createdBy: number | null;
  version: RowVersion;
  saleName?: string | null;
  teamName?: string | null;
  createdByName?: string | null;
}
export interface PeriodLockDto {
  id: number;
  scheduleId: number;
  scheduleCode: string;
  periodFrom: DateOnly;
  periodTo: DateOnly;
  reason: string;
  isActive: boolean;
  createdBy: number | null;
  createdByName: string | null;
  createdAtUtc: UtcDateTime;
  releasedAtUtc: UtcDateTime | null;
  releasedBy: number | null;
  releasedByName: string | null;
  releaseReason: string | null;
  version: RowVersion;
}
export interface ScheduleDetail {
  schedule: ScheduleDto;
  history: ScheduleDto[];
  periodLocks?: PeriodLockDto[] | null;
}
export interface UpsertScheduleRequest {
  scheduleCode?: string | null;
  name: string;
  payeeType: string;
  saleUserId?: number | null;
  teamId?: number | null;
  policyVersionId?: number | null;
  scheduleType: string;
  calculationBasis?: string | null;
  waitDays: number;
  cutoffDay: number;
  payoutDay: number;
  payoutMonthOffset: number;
  payoutDayRule: string;
  holidayAdjustment: string;
  timeZoneId?: string | null;
  minPayoutAmount?: Decimal | null;
  eligibilityConditions?: string[] | null;
  effectiveFrom: DateOnly;
  effectiveTo?: DateOnly | null;
  changeReason?: string | null;
  version?: RowVersion | null;
}
export interface PreviewSale {
  saleUserId: number;
  saleName: string;
  itemCount: number;
  amount: Decimal;
  belowMinPayout: boolean;
}
export interface PreviewPeriod {
  periodKey: string;
  from: DateOnly;
  to: DateOnly;
  payoutDate: DateOnly;
  itemCount: number;
  projectedAmount: Decimal;
  sales: PreviewSale[];
}
export interface SchedulePreview {
  scheduleId: number;
  scheduleCode: string;
  scheduleType: string;
  minPayout: Decimal;
  periods: PreviewPeriod[];
  unscheduledPendingAmount: Decimal;
  note: string;
}
export interface CalendarEntry {
  date: DateOnly;
  kind: 'CUTOFF' | 'PLANNED_PAYOUT' | 'PAYOUT' | 'HOLIDAY' | string;
  scheduleId: number | null;
  scheduleCode: string | null;
  payoutId: number | null;
  payoutCode: string | null;
  saleUserId: number | null;
  /** PLANNED_PAYOUT: projected net for the period (server preview), null when past / not projectable. */
  amount: Decimal | null;
  status: string;
  periodKey: string | null;
  saleName?: string | null;
  isProjected?: boolean;
  itemCount?: number | null;
  isLocked?: boolean;
}
export interface Holiday {
  id: number;
  date: DateOnly;
  name: string;
}

// ---------------------------------------------------------------- payouts

export interface PayoutListItem {
  id: number;
  payoutCode: string;
  saleUserId: number;
  saleName: string;
  scheduleId: number;
  scheduleCode: string;
  scheduleVersionNo: number;
  periodKey: string;
  periodFrom: DateOnly;
  periodTo: DateOnly;
  plannedPayoutDate: DateOnly;
  amount: Decimal;
  currency: string;
  lineCount: number;
  status: string;
  statusName: string;
  createdAtUtc: UtcDateTime;
  paidAtUtc: UtcDateTime | null;
  version: RowVersion;
}
export interface PayoutLine {
  id: number;
  lineType: 'ACCRUAL' | 'ADJUSTMENT' | string;
  accrualId: number | null;
  adjustmentId: number | null;
  amount: Decimal;
  currency: string;
  readyOn: DateOnly;
  explanation: string;
  contractId: number | null;
  receiptAllocationId: number | null;
  policyVersionId: number | null;
  releasedAtUtc: UtcDateTime | null;
  releaseReason: string | null;
}
export interface PayoutVerification {
  id: number;
  attemptNo: number;
  verifierId: number;
  outcome: 'SUCCESS' | 'FAILED' | string;
  bankRef: string | null;
  verifiedAmount: Decimal | null;
  evidenceDocumentIds: number[];
  evidenceRef: string | null;
  failureCode: string | null;
  note: string | null;
  verifiedAtUtc: UtcDateTime;
  verifierName?: string | null;
}
export interface PayoutDetail {
  payout: PayoutListItem;
  approvalInstanceId: number | null;
  submittedBy: number | null;
  submittedAtUtc: UtcDateTime | null;
  approvedBy: number | null;
  approvedAtUtc: UtcDateTime | null;
  executedBy: number | null;
  executedAtUtc: UtcDateTime | null;
  paymentOrderRef: string | null;
  attemptNo: number;
  bankPaymentRef: string | null;
  failureCode: string | null;
  failureReason: string | null;
  cancelReason: string | null;
  lines: PayoutLine[];
  verifications: PayoutVerification[];
  reversals: AdjustmentDto[];
  /** User who generated the payout (null = background worker). The server forbids them from verifying it. */
  createdBy?: number | null;
  createdByName?: string | null;
  submittedByName?: string | null;
  approvedByName?: string | null;
  executedByName?: string | null;
}
export interface PayoutRun {
  id: number;
  asOfDate: DateOnly;
  mode: string;
  triggeredBy: number | null;
  scheduleId: number | null;
  periodKey: string | null;
  startedAtUtc: UtcDateTime;
  finishedAtUtc: UtcDateTime | null;
  createdCount: number;
  existingCount: number;
  carriedForwardCount: number;
  failedCount: number;
  triggeredByName?: string | null;
}
export interface GenerationResultLine {
  saleUserId: number;
  scheduleId: number | null;
  scheduleCode: string | null;
  periodKey: string | null;
  outcome: string;
  payoutId: number | null;
  amount: Decimal | null;
  itemCount: number;
  detail: string | null;
}
export interface GenerationRun {
  runId: number;
  asOfDate: DateOnly;
  mode: string;
  created: number;
  existing: number;
  carriedForward: number;
  failed: number;
  results: GenerationResultLine[];
}
export interface VerifyPayoutRequest {
  outcome: 'SUCCESS' | 'FAILED';
  bankPaymentRef?: string | null;
  verifiedAmount?: Decimal | null;
  evidenceDocumentIds?: number[] | null;
  evidenceRef?: string | null;
  failureCode?: string | null;
  note?: string | null;
  version: RowVersion;
}

// ---------------------------------------------------------------- read models

export interface StatusTotal {
  status: string;
  statusName: string;
  count: number;
  amount: Decimal;
}
export interface UpcomingPayout {
  scheduleId: number;
  scheduleCode: string;
  periodKey: string;
  payoutDate: DateOnly;
  projectedAmount: Decimal;
  itemCount: number;
  belowMinPayout: boolean;
}
export interface MySummary {
  saleUserId: number;
  currency: string;
  accruals: StatusTotal[];
  paidTotal: Decimal;
  pendingClawbacks: Decimal;
  openPayouts: PayoutListItem[];
  projected: UpcomingPayout[];
  history: { payout: PayoutListItem; lines: PayoutLine[] }[];
  recentAccruals: AccrualListItem[];
}
export interface DeskSummary {
  policies: StatusTotal[];
  accruals: StatusTotal[];
  schedules: StatusTotal[];
  payouts: StatusTotal[];
  recentRuns: PayoutRun[];
  disputes: { contractId: number; openReceivables: number; heldAmount: Decimal }[];
  exceptions: SourceEvent[];
}

type Key = { idempotencyKey: string };

export const commissionApi = {
  // policies
  policies: (q?: { status?: string; q?: string }) => http.get<PolicyListItem[]>(`${base}/policies`, q),
  policy: (id: number) => http.get<PolicyDetail>(`${base}/policies/${id}`),
  createPolicy: (req: CreatePolicyRequest, o: Key) => http.post<PolicyDetail>(`${base}/policies`, req, o),
  copyPolicy: (id: number, req: { code?: string | null; name: string; fromVersionId?: number | null }, o: Key) =>
    http.post<PolicyDetail>(`${base}/policies/${id}/copies`, req, o),
  retirePolicy: (id: number, reason: string, o: Key) => http.post<{ id: number }>(`${base}/policies/${id}/retirements`, { reason }, o),
  policyVersion: (id: number, vid: number) => http.get<PolicyVersionDto>(`${base}/policies/${id}/versions/${vid}`),
  newPolicyVersion: (id: number, req: { fromVersionId?: number | null; changeNote?: string | null }, o: Key) =>
    http.post<PolicyVersionDto>(`${base}/policies/${id}/versions`, req, o),
  updatePolicyVersion: (id: number, vid: number, req: UpdatePolicyVersionRequest) =>
    http.patch<PolicyVersionDto>(`${base}/policies/${id}/versions/${vid}`, req),
  policyConflicts: (id: number, vid: number) => http.get<PolicyConflict[]>(`${base}/policies/${id}/versions/${vid}/conflicts`),
  submitPolicyVersion: (id: number, vid: number, note: string | null, o: Key) =>
    http.post<ApprovalInstanceRef>(`${base}/policies/${id}/versions/${vid}/approval-requests`, { note }, o),
  retirePolicyVersion: (id: number, vid: number, reason: string, o: Key) =>
    http.post<{ id: number }>(`${base}/policies/${id}/versions/${vid}/retirements`, { reason }, o),

  // accruals
  accruals: (f: AccrualFilters) => http.get<PagedResult<AccrualListItem>>(`${base}/accruals`, { ...f }),
  accrual: (id: number) => http.get<AccrualDetail>(`${base}/accruals/${id}`),
  confirmAccruals: (accrualIds: number[], note: string | null, o: Key) =>
    http.post<{ confirmed: number }>(`${base}/accruals/confirmations`, { accrualIds, note }, o),
  holdAccrual: (id: number, req: { reason: string; reviewAtUtc?: string | null; version: RowVersion }, o: Key) =>
    http.post<{ id: number }>(`${base}/accruals/${id}/holds`, req, o),
  releaseAccrual: (id: number, req: { reason: string; version: RowVersion }, o: Key) =>
    http.post<{ id: number }>(`${base}/accruals/${id}/hold-releases`, req, o),
  sourceEvents: (q: { contractId?: number; receiptId?: number; outcome?: string }) => http.get<SourceEvent[]>(`${base}/source-events`, q),

  // adjustments
  /** dateField: createdAt (default) | decidedAt. */
  adjustments: (q: { saleUserId?: number; status?: string; page?: number; pageSize?: number } & DateRangeParams) =>
    http.get<PagedResult<AdjustmentDto>>(`${base}/adjustments`, q),
  adjustment: (id: number) => http.get<AdjustmentDto>(`${base}/adjustments/${id}`),
  createAdjustment: (req: CreateAdjustmentRequest, o: Key) => http.post<AdjustmentDto>(`${base}/adjustments`, req, o),
  submitAdjustment: (id: number, note: string | null, o: Key) =>
    http.post<ApprovalInstanceRef>(`${base}/adjustments/${id}/approval-requests`, { note }, o),
  cancelAdjustment: (id: number, reason: string, o: Key) => http.post<{ id: number }>(`${base}/adjustments/${id}/cancellations`, { reason }, o),

  // schedules
  schedules: (q?: { status?: string; saleUserId?: number; scheduleType?: string; code?: string }) =>
    http.get<ScheduleDto[]>(`${base}/payout-schedules`, q),
  schedule: (id: number) => http.get<ScheduleDetail>(`${base}/payout-schedules/${id}`),
  createSchedule: (req: UpsertScheduleRequest, o: Key) => http.post<ScheduleDto>(`${base}/payout-schedules`, req, o),
  updateSchedule: (id: number, req: UpsertScheduleRequest) => http.patch<ScheduleDto>(`${base}/payout-schedules/${id}`, req),
  newScheduleVersion: (id: number, req: { changeReason: string; effectiveFrom?: DateOnly | null }, o: Key) =>
    http.post<ScheduleDto>(`${base}/payout-schedules/${id}/versions`, req, o),
  submitSchedule: (id: number, note: string | null, o: Key) =>
    http.post<ApprovalInstanceRef>(`${base}/payout-schedules/${id}/approval-requests`, { note }, o),
  changeScheduleState: (id: number, action: 'pauses' | 'resumptions' | 'cancellations', req: { reason: string; version: RowVersion }, o: Key) =>
    http.post<ScheduleDto>(`${base}/payout-schedules/${id}/${action}`, req, o),
  schedulePreview: (id: number, periods = 3) => http.get<SchedulePreview>(`${base}/payout-schedules/${id}/preview`, { periods }),
  calendar: (from: DateOnly, to: DateOnly) => http.get<CalendarEntry[]>(`${base}/calendar`, { from, to }),
  periodLocks: (id: number) => http.get<PeriodLockDto[]>(`${base}/payout-schedules/${id}/period-locks`),
  lockPeriod: (id: number, req: { periodFrom: DateOnly; periodTo: DateOnly; reason: string }, o: Key) =>
    http.post<PeriodLockDto>(`${base}/payout-schedules/${id}/period-locks`, req, o),
  releasePeriodLock: (id: number, lockId: number, req: { reason: string; version: RowVersion }, o: Key) =>
    http.post<PeriodLockDto>(`${base}/payout-schedules/${id}/period-locks/${lockId}/releases`, req, o),
  holidays: (year?: number) => http.get<Holiday[]>(`${base}/holidays`, { year }),
  addHoliday: (req: { date: DateOnly; name: string }) => http.post<Holiday>(`${base}/holidays`, req),
  removeHoliday: (id: number) => http.delete<void>(`${base}/holidays/${id}`),

  // payouts
  /** dateField: plannedPayoutDate (default) | periodFrom | paidAt | createdAt. */
  payouts: (q: { saleUserId?: number; status?: string; periodKey?: string; scheduleId?: number; page?: number; pageSize?: number } & DateRangeParams) =>
    http.get<PagedResult<PayoutListItem>>(`${base}/payouts`, q),
  payout: (id: number) => http.get<PayoutDetail>(`${base}/payouts/${id}`),
  generatePayouts: (req: { asOfDate?: DateOnly | null; scheduleId?: number | null; periodKey?: string | null }, o: Key) =>
    http.post<GenerationRun>(`${base}/payouts/generation`, req, o),
  payoutRuns: (take = 20) => http.get<PayoutRun[]>(`${base}/payout-runs`, { take }),
  submitPayout: (id: number, note: string | null, o: Key) => http.post<ApprovalInstanceRef>(`${base}/payouts/${id}/approval-requests`, { note }, o),
  executePayout: (id: number, req: { paymentOrderRef?: string | null; note?: string | null; version: RowVersion }, o: Key) =>
    http.post<PayoutDetail>(`${base}/payouts/${id}/executions`, req, o),
  verifyPayout: (id: number, req: VerifyPayoutRequest, o: Key) => http.post<PayoutDetail>(`${base}/payouts/${id}/verifications`, req, o),
  retryPayout: (id: number, req: { reason: string; version: RowVersion }, o: Key) => http.post<PayoutDetail>(`${base}/payouts/${id}/retries`, req, o),
  cancelPayout: (id: number, req: { reason: string; version: RowVersion }, o: Key) =>
    http.post<PayoutDetail>(`${base}/payouts/${id}/cancellations`, req, o),
  reversePayout: (id: number, req: { amount?: Decimal | null; reasonText: string; evidenceDocumentIds?: number[] | null }, o: Key) =>
    http.post<AdjustmentDto>(`${base}/payouts/${id}/reversals`, req, o),

  // reads
  mySummary: () => http.get<MySummary>(`${base}/me/summary`),
  desk: () => http.get<DeskSummary>(`${base}/desk/summary`),
};

/** Query keys — first segment 'commission' so a mutation can invalidate the whole domain. */
export const ck = {
  all: ['commission'] as const,
  policies: (q: object) => ['commission', 'policies', q] as const,
  policy: (id: number) => ['commission', 'policy', id] as const,
  conflicts: (id: number, vid: number) => ['commission', 'policy', id, 'conflicts', vid] as const,
  accruals: (f: object) => ['commission', 'accruals', f] as const,
  accrual: (id: number) => ['commission', 'accrual', id] as const,
  adjustments: (q: object) => ['commission', 'adjustments', q] as const,
  schedules: (q: object) => ['commission', 'schedules', q] as const,
  schedule: (id: number) => ['commission', 'schedule', id] as const,
  preview: (id: number, n: number) => ['commission', 'schedule', id, 'preview', n] as const,
  calendar: (from: string, to: string) => ['commission', 'calendar', from, to] as const,
  holidays: (year: number) => ['commission', 'holidays', year] as const,
  payouts: (q: object) => ['commission', 'payouts', q] as const,
  payout: (id: number) => ['commission', 'payout', id] as const,
  runs: ['commission', 'runs'] as const,
  me: ['commission', 'me'] as const,
  desk: ['commission', 'desk'] as const,
};
